/**
 * Sankey Diagram Component - OPTIMIZED VERSION
 * Clean, insightful, and professional user journey visualization
 * Features:
 * - Smart URL shortening for readability
 * - Top N filtering to show only key paths
 * - Full-width responsive layout
 * - Clear labels and metrics
 */

import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal, sankeyJustify, sankeyLeft, SankeyNode, SankeyLink } from 'd3-sankey';

interface Node {
  name: string;
  id: string;
  sessions?: string[];  // All session IDs (including stitched) for drill-down
  journeyCount?: number;  // Number of unique STITCHED journeys (for display)
  utmSources?: { source: string; count: number }[];
  utmCampaigns?: { campaign: string; count: number }[];
}

interface Link {
  source: string;
  target: string;
  value: number;
  sessions: string[];
}

interface LoopPattern {
  fromPage: string;
  toPage: string;
  sessions: number;
  percentage: number;
}

interface SankeyDiagramProps {
  nodes: Node[];
  links: Link[];
  onNodeClick?: (node: Node) => void;
  onLinkClick?: (link: Link) => void;
  onLoopsDetected?: (loops: LoopPattern[]) => void; // NEW: Callback for loop analysis
  height?: number;
  maxNodes?: number; // Limit nodes for clarity
  minSessions?: number; // Filter out low-traffic paths
}

const SankeyDiagram = ({
  nodes,
  links,
  onNodeClick,
  onLinkClick,
  onLoopsDetected,
  height = 800, // Increased default height
  maxNodes = 50, // Show top 50 nodes by default (balanced view)
  minSessions = 2, // Minimum 2 sessions to show
}: SankeyDiagramProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height }); // Start with 0, will be calculated
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  // Store callbacks in refs to prevent infinite re-renders
  const onNodeClickRef = useRef(onNodeClick);
  const onLinkClickRef = useRef(onLinkClick);
  const onLoopsDetectedRef = useRef(onLoopsDetected);

  // Update refs when callbacks change
  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
    onLinkClickRef.current = onLinkClick;
    onLoopsDetectedRef.current = onLoopsDetected;
  }, [onNodeClick, onLinkClick, onLoopsDetected]);

  // **PERFORMANCE: Memoize filtered and optimized data**
  const optimizedData = useMemo(() => {
    if (nodes.length === 0 || links.length === 0) {
      return { nodes: [], links: [] };
    }

    // STEP 1: Sort nodes and links for deterministic rendering
    const sortedNodes = [...nodes].sort((a, b) => a.id.localeCompare(b.id));
    const sortedLinks = [...links].sort((a, b) => {
      const sourceCompare = a.source.localeCompare(b.source);
      if (sourceCompare !== 0) return sourceCompare;
      return a.target.localeCompare(b.target);
    });

    // STEP 2: Filter links by minimum sessions
    const filteredLinks = sortedLinks.filter(l => l.value >= minSessions);

    // STEP 3: Calculate node importance (total traffic)
    const nodeTraffic = new Map<string, number>();
    filteredLinks.forEach(l => {
      nodeTraffic.set(l.source, (nodeTraffic.get(l.source) || 0) + l.value);
      nodeTraffic.set(l.target, (nodeTraffic.get(l.target) || 0) + l.value);
    });

    // STEP 4: Get top N nodes by traffic (with stable sort by ID as tiebreaker)
    const topNodes = Array.from(nodeTraffic.entries())
      .sort((a, b) => {
        const trafficDiff = b[1] - a[1];
        if (trafficDiff !== 0) return trafficDiff;
        return a[0].localeCompare(b[0]); // Stable tiebreaker
      })
      .slice(0, maxNodes)
      .map(([id]) => id);

    // STEP 5: Filter nodes and links to top N
    const topNodeSet = new Set(topNodes);
    const optimizedNodes = sortedNodes
      .filter(n => topNodeSet.has(n.id))
      .map(n => ({
        ...n,
        name: n.name, // Use as-is (already formatted by backend)
        originalName: n.name, // Keep original for tooltips
      }));

    const optimizedLinks = filteredLinks.filter(
      l => topNodeSet.has(l.source) && topNodeSet.has(l.target)
    );

    console.log('🔄 Optimized data (sorted for stability):', {
      nodes: optimizedNodes.length,
      links: optimizedLinks.length,
      firstNode: optimizedNodes[0]?.id,
      lastNode: optimizedNodes[optimizedNodes.length - 1]?.id,
    });

    return { nodes: optimizedNodes, links: optimizedLinks };
  }, [nodes, links, minSessions, maxNodes]); // Only recompute when these change

  // **CRITICAL: Smart URL shortening for readability**
  const shortenURL = (url: string): string => {
    // Remove domain and protocol
    let path = url.replace(/^https?:\/\/[^\/]+/, '');

    // Remove query params and hashes for cleaner display
    path = path.split('?')[0].split('#')[0];

    // Remove trailing slashes
    path = path.replace(/\/+$/, '');

    // If empty, it's the homepage
    if (!path || path === '/') return 'Homepage';

    // Extract meaningful parts
    const parts = path.split('/').filter(p => p);

    // Handle common patterns
    if (parts.includes('online-clinic')) {
      if (parts.includes('consultation')) {
        const consultationIndex = parts.indexOf('consultation');
        const condition = parts[consultationIndex + 1]; // e.g., "weight-loss"
        const step = parts[consultationIndex + 2]; // e.g., "1", "2", "preliminary", "end"

        if (step) {
          // Handle different step formats
          if (step === 'preliminary') return `Consultation: Start`;
          if (step === 'end') return `Consultation: Complete`;
          if (!isNaN(parseInt(step))) return `Consultation: Step ${step}`;
          return `Consultation: ${step}`;
        }

        return `Consultation: ${condition || 'Form'}`;
      }

      // Handle weight-loss specific pages
      if (parts.includes('weight-loss')) {
        const productIndex = parts.indexOf('weight-loss') + 1;
        if (productIndex < parts.length) {
          const product = parts[productIndex];
          return `Weight Loss: ${product}`;
        }
        return 'Weight Loss';
      }

      return 'Online Clinic';
    }

    if (parts.includes('shop') || parts.includes('products')) {
      const product = parts[parts.length - 1];
      return `Product: ${product}`;
    }

    if (parts.includes('blog')) {
      const article = parts[parts.length - 1];
      return `Blog: ${article.substring(0, 30)}...`;
    }

    if (parts.includes('checkout')) return 'Checkout';
    if (parts.includes('basket') || parts.includes('cart')) return 'Cart';
    if (parts.includes('payment-confirmed')) return 'Payment Confirmed';
    if (parts.includes('accounts')) {
      if (parts.includes('login')) return 'Login';
      if (parts.includes('signup')) return 'Sign Up';
      return 'Account';
    }

    // For other pages, use last meaningful part
    const lastPart = parts[parts.length - 1];
    if (lastPart && lastPart.length < 30) {
      return lastPart.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    // Fallback: show first and last part
    if (parts.length > 2) {
      return `${parts[0]}/.../$ {parts[parts.length - 1]}`;
    }

    return parts.join(' / ');
  };

  // Calculate diagram dimensions based on number of stages
  // Each stage gets equal width (250px) for better readability
  const calculateDynamicWidth = (nodes: any[], links: any[]): number => {
    if (nodes.length === 0 || links.length === 0) return 1200;

    // Build a graph to find the longest path (max depth)
    const nodeDepths = new Map<string, number>();
    const adjacencyList = new Map<string, string[]>();

    // Initialize adjacency list
    nodes.forEach(n => adjacencyList.set(n.id, []));
    links.forEach(l => {
      const neighbors = adjacencyList.get(l.source) || [];
      neighbors.push(l.target);
      adjacencyList.set(l.source, neighbors);
    });

    // BFS to find max depth (number of stages)
    const visited = new Set<string>();
    const queue: { node: string; depth: number }[] = [];

    // Find all starting nodes (nodes with no incoming edges)
    const hasIncoming = new Set(links.map(l => l.target));
    const startNodes = nodes.filter(n => !hasIncoming.has(n.id));

    startNodes.forEach(n => {
      queue.push({ node: n.id, depth: 0 });
      nodeDepths.set(n.id, 0);
    });

    let maxDepth = 0;
    while (queue.length > 0) {
      const { node, depth } = queue.shift()!;
      if (visited.has(node)) continue;
      visited.add(node);

      maxDepth = Math.max(maxDepth, depth);
      nodeDepths.set(node, depth);

      const neighbors = adjacencyList.get(node) || [];
      neighbors.forEach(neighbor => {
        if (!visited.has(neighbor)) {
          queue.push({ node: neighbor, depth: depth + 1 });
        }
      });
    }

    // Number of stages (columns) = maxDepth + 1
    const numStages = maxDepth + 1;

    // MINIMUM 350px width per stage (user requirement)
    const minWidthPerStage = 350;

    // Add margins (left + right = 300px) PLUS extra space for labels (150px - more accurate)
    const margins = 300;
    const labelSpace = 150; // Reduced: just enough space for node labels extending beyond nodes

    // Width scales infinitely: each stage gets EXACTLY 350px minimum
    const calculatedWidth = numStages * minWidthPerStage + margins + labelSpace;

    // Ensure minimum width is at least screen width (or window width)
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const finalWidth = Math.max(calculatedWidth, screenWidth);

    console.log(
      `📐 Sankey Width: ${numStages} stages × ${minWidthPerStage}px + ${margins}px margins + ${labelSpace}px labels = ${calculatedWidth}px | screen: ${screenWidth}px | FINAL: ${finalWidth}px`
    );

    return finalWidth;
  };

  // Update dimensions when data changes
  useEffect(() => {
    if (optimizedData.nodes.length === 0 || optimizedData.links.length === 0) return;

    const dynamicWidth = calculateDynamicWidth(optimizedData.nodes, optimizedData.links);
    setDimensions({ width: dynamicWidth, height });
  }, [optimizedData, height]);

  // Render Sankey diagram
  useEffect(() => {
    if (!svgRef.current || optimizedData.nodes.length === 0 || optimizedData.links.length === 0)
      return;

    const { nodes: optimizedNodes, links: optimizedLinks } = optimizedData;

    console.log(
      `📊 Sankey Optimization: ${nodes.length} → ${optimizedNodes.length} nodes, ${links.length} → ${optimizedLinks.length} links`
    );

    // CRITICAL: Use the calculated dynamic width, not container width!
    const dynamicWidth = calculateDynamicWidth(optimizedNodes, optimizedLinks);
    const { height } = dimensions;

    // Fixed margins for equal-width stages
    const margin = {
      top: 40,
      right: 150, // Fixed margin for consistent stage width
      bottom: 40,
      left: 150, // Fixed margin for consistent stage width
    };

    console.log(
      `📐 Rendering Sankey with width: ${dynamicWidth}px (dynamic) vs container: ${dimensions.width}px`
    );

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    // Create SVG with explicit dimensions - USE DYNAMIC WIDTH!
    const svg = d3
      .select(svgRef.current)
      .attr('width', dynamicWidth) // Use calculated dynamic width, NOT container width!
      .attr('height', height)
      .style('min-width', `${dynamicWidth}px`) // Force minimum width
      .style('display', 'block');

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Use dynamic width for chart calculations
    const chartWidth = dynamicWidth - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    console.log(`📊 Chart dimensions: ${chartWidth}px × ${chartHeight}px`);

    // Create node and link index maps (using optimized data)
    const nodeById = new Map(optimizedNodes.map(d => [d.id, d]));
    const nodeIndexById = new Map(optimizedNodes.map((d, i) => [d.id, i]));

    // Filter out basic invalid links (self-loops and missing nodes)
    const basicValidLinks = optimizedLinks.filter(l => {
      // Remove self-links (where source === target)
      if (l.source === l.target) {
        return false;
      }
      // Ensure both source and target nodes exist in optimized set
      const sourceExists = nodeById.has(l.source);
      const targetExists = nodeById.has(l.target);
      if (!sourceExists || !targetExists) {
        return false;
      }
      return true;
    });

    // Convert to indexed links for cycle detection
    const indexedLinks = basicValidLinks.map(l => ({
      source: nodeIndexById.get(l.source)!,
      target: nodeIndexById.get(l.target)!,
      value: l.value,
      sessions: l.sessions,
      originalSource: l.source,
      originalTarget: l.target,
    }));

    // **SMART CYCLE HANDLING: Remove back edges for d3-sankey compatibility**
    // Problem: d3-sankey library CANNOT handle cycles (throws "circular link" error)
    // Solution: Remove back edges (reverse navigation) but keep forward flow (first visit)
    // This shows the PRIMARY user journey path while satisfying d3-sankey requirements
    const handleCyclesIntelligently = (links: any[], nodeCount: number) => {
      // Build adjacency list
      const adjacencyList: Map<number, Set<number>> = new Map();
      for (let i = 0; i < nodeCount; i++) {
        adjacencyList.set(i, new Set());
      }
      links.forEach(l => adjacencyList.get(l.source)!.add(l.target));

      // Find ONLY back edges (edges that create cycles in DFS tree)
      const visited = new Set<number>();
      const recursionStack = new Set<number>();
      const backEdges = new Set<string>();

      const dfs = (node: number): void => {
        visited.add(node);
        recursionStack.add(node);

        const neighbors = adjacencyList.get(node) || new Set();
        for (const neighbor of neighbors) {
          if (!visited.has(neighbor)) {
            dfs(neighbor);
          } else if (recursionStack.has(neighbor)) {
            // This is a back edge (causes cycle)
            backEdges.add(`${node}→${neighbor}`);
          }
        }

        recursionStack.delete(node);
      };

      // Run DFS from all nodes
      for (let i = 0; i < nodeCount; i++) {
        if (!visited.has(i)) {
          dfs(i);
        }
      }

      // STRATEGY: Remove ALL back edges to satisfy d3-sankey requirements
      // d3-sankey CANNOT handle ANY cycles, so we must remove all back edges
      // We prioritize keeping FORWARD flow (first occurrence in user journey)
      const removedBackEdges: any[] = []; // Track removed edges for loop analysis

      const processedLinks = links.filter(l => {
        const edgeKey = `${l.source}→${l.target}`;
        const isBackEdge = backEdges.has(edgeKey);

        if (isBackEdge) {
          // Store this back edge for loop analysis
          removedBackEdges.push({
            fromPage: l.originalSource,
            toPage: l.originalTarget,
            sessions: l.value,
          });
          return false;
        }

        return true; // Keep all forward edges
      });

      return { links: processedLinks, removedBackEdges };
    };

    // Apply intelligent cycle handling
    const { links: finalLinks, removedBackEdges } = handleCyclesIntelligently(
      indexedLinks,
      optimizedNodes.length
    );

    // Calculate total sessions for percentage
    const totalSessions = optimizedData.links.reduce((sum, l) => sum + l.value, 0);

    // Convert removed back edges to loop patterns and notify parent
    if (onLoopsDetected && removedBackEdges.length > 0) {
      const loopPatterns: LoopPattern[] = removedBackEdges.map(edge => ({
        fromPage: edge.fromPage,
        toPage: edge.toPage,
        sessions: edge.sessions,
        percentage: (edge.sessions / totalSessions) * 100,
      }));
      onLoopsDetectedRef.current?.(loopPatterns);
    }

    // Prepare Sankey data with indexes
    const sankeyData: any = {
      nodes: optimizedNodes.map((d, i) => ({ ...d, index: i })),
      links: finalLinks.map(l => ({
        source: l.source,
        target: l.target,
        value: l.value,
        sessions: l.sessions,
      })),
    };

    // Create Sankey layout with equal-width stages
    const nodeCount = optimizedNodes.length;
    // Adaptive padding: more nodes = less padding, but never too tight
    const optimalPadding = Math.max(15, Math.min(60, (chartHeight / nodeCount) * 0.8));

    // Fixed node width for consistent appearance across all diagrams
    const nodeWidth = 24; // Fixed 24px width for all nodes

    const sankeyGenerator = sankey<any, any>()
      .nodeId((d: any) => d.index)
      .nodeWidth(nodeWidth) // Fixed width for consistency
      .nodePadding(optimalPadding) // Adaptive padding based on node count
      .nodeAlign(sankeyLeft) // **FIXED: Left align - entry nodes start on the left**
      .nodeSort((a: any, b: any) => {
        // Sort nodes within each column alphabetically for stable positioning
        return a.name.localeCompare(b.name);
      })
      .extent([
        [0, 0],
        [chartWidth, chartHeight],
      ])
      .iterations(32); // Good balance of performance and quality

    let sankeyNodes: any[] = [];
    let sankeyLinks: any[] = [];

    try {
      const result = sankeyGenerator(sankeyData);
      sankeyNodes = result.nodes;
      sankeyLinks = result.links;
      console.log(
        `✅ Sankey layout computed: ${sankeyNodes.length} nodes, ${sankeyLinks.length} links`
      );

      // CRITICAL FIX: Manually adjust node X positions for EQUAL-WIDTH STAGES
      // d3-sankey doesn't respect our desired equal spacing, so we override it

      // 1. Find the maximum depth (number of stages)
      const maxDepth = Math.max(...sankeyNodes.map((n: any) => n.depth));
      const numStages = maxDepth + 1;

      // 2. Calculate equal stage width (MINIMUM 300px per stage)
      const minStageWidth = 300;
      const stageWidth = Math.max(minStageWidth, chartWidth / numStages);

      console.log(
        `📐 Forcing equal stages: ${numStages} stages × ${stageWidth.toFixed(
          0
        )}px (min ${minStageWidth}px) | chartWidth: ${chartWidth}px | dynamicWidth: ${dynamicWidth}px`
      );

      // 3. Reposition nodes to have equal stage spacing
      const nodesByDepth = new Map<number, number>();
      sankeyNodes.forEach((node: any, index: number) => {
        const oldX0 = node.x0;

        // Calculate new x position based on depth (stage)
        const newX0 = node.depth * stageWidth;
        const newX1 = newX0 + nodeWidth;

        // Update node positions
        node.x0 = newX0;
        node.x1 = newX1;

        // Track nodes per depth
        nodesByDepth.set(node.depth, (nodesByDepth.get(node.depth) || 0) + 1);

        // Debug first and last nodes
        if (index === 0) {
          console.log(
            `📍 First node (depth ${node.depth}): OLD x0=${oldX0.toFixed(
              0
            )} → NEW x0=${newX0.toFixed(0)}`
          );
        }
        if (index === sankeyNodes.length - 1) {
          console.log(
            `📍 Last node (depth ${node.depth}): OLD x0=${oldX0.toFixed(
              0
            )} → NEW x0=${newX0.toFixed(0)}`
          );
        }
      });

      // Calculate the actual rightmost node position
      const maxNodeX = Math.max(...sankeyNodes.map((n: any) => n.x1));
      const actualSpread = maxNodeX + margin.right; // Include right margin

      // CRITICAL FIX: Update SVG width to match actual node spread!
      // Add 200px for labels that extend beyond the rightmost nodes
      const correctedWidth = Math.max(dynamicWidth, actualSpread + 200);

      console.log(`✅ Equal-width stages applied!`, {
        numStages,
        stageWidth: stageWidth.toFixed(0),
        calculatedWidth: (numStages * stageWidth).toFixed(0),
        maxNodeX: maxNodeX.toFixed(0),
        actualSpread: actualSpread.toFixed(0),
        dynamicWidth,
        correctedWidth,
        chartWidth,
        nodesPerDepth: Array.from(nodesByDepth.entries())
          .map(([d, c]) => `D${d}:${c}`)
          .join(', '),
      });

      // Update SVG width to corrected value
      svg.attr('width', correctedWidth).style('min-width', `${correctedWidth}px`);

      // CRITICAL: Also update the wrapper div width via container reference
      if (containerRef.current) {
        const wrapper = containerRef.current.querySelector('div') as HTMLDivElement;
        if (wrapper) {
          wrapper.style.width = `${correctedWidth}px`;
          wrapper.style.minWidth = `${correctedWidth}px`;
          console.log(`📦 Updated wrapper div width to ${correctedWidth}px`);
        }
      }
    } catch (error) {
      console.error('❌ Sankey error:', error, 'Data:', sankeyData);
      // Show error message in the diagram
      g.append('text')
        .attr('x', chartWidth / 2)
        .attr('y', chartHeight / 2)
        .attr('text-anchor', 'middle')
        .attr('class', 'fill-red-500 text-lg')
        .text('Error rendering diagram. Check console for details.');
      return;
    }

    // Sanity check for NaN values
    if (sankeyNodes.some(n => isNaN(n.x0) || isNaN(n.y0))) {
      console.error('❌ NaN detected in node positions!', sankeyNodes);
      return;
    }

    // Enhanced color scale with better visibility
    const isDarkMode = document.documentElement.classList.contains('dark');

    // Vibrant color palette that works in both light and dark mode
    const colorPalette = [
      '#3B82F6', // Blue
      '#10B981', // Green
      '#F59E0B', // Amber
      '#EF4444', // Red
      '#8B5CF6', // Purple
      '#EC4899', // Pink
      '#14B8A6', // Teal
      '#F97316', // Orange
      '#06B6D4', // Cyan
      '#6366F1', // Indigo
    ];

    // **CRITICAL FIX: Stable color assignment based on URL hash**
    // This ensures colors don't change randomly on every render
    const hashString = (str: string): number => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return Math.abs(hash);
    };

    const getStableColor = (pageName: string): string => {
      const hash = hashString(pageName);
      const colorIndex = hash % colorPalette.length;
      return colorPalette[colorIndex];
    };

    // Create stable color mapping for all nodes
    const nodeColorMap = new Map<string, string>();
    optimizedNodes.forEach(node => {
      nodeColorMap.set(node.name, getStableColor(node.id));
    });

    // Draw links with gradient effect
    const link = g
      .append('g')
      .selectAll('path')
      .data(sankeyLinks)
      .enter()
      .append('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke', (d: any) => {
        const sourceNode = sankeyNodes[d.source.index];
        return nodeColorMap.get(sourceNode.name) || colorPalette[0]; // Use stable color!
      })
      .attr('stroke-width', (d: any) => Math.max(2, d.width)) // Minimum width 2px
      .attr('fill', 'none')
      .attr('opacity', isDarkMode ? 0.5 : 0.4) // Better visibility in dark mode
      .attr('class', 'sankey-link')
      .style('cursor', 'default') // **CHANGED: Not clickable anymore**
      .style('pointer-events', 'all') // Keep for hover effects
      .style('transition', 'all 0.3s ease')
      .on('mouseover', function (event, d: any) {
        const sourceNode = nodes[d.source.index];
        const targetNode = nodes[d.target.index];
        setHoveredLink(`${sourceNode.id}→${targetNode.id}`);

        d3.select(this)
          .attr('opacity', 0.9) // Brighter on hover
          .attr('stroke-width', Math.max(4, d.width + 4)); // Wider on hover
      })
      .on('mouseout', function (event, d: any) {
        setHoveredLink(null);
        d3.select(this)
          .attr('opacity', isDarkMode ? 0.5 : 0.4)
          .attr('stroke-width', Math.max(2, d.width));
      })
      // **REMOVED: Link clicks disabled per user request**
      // Users should click node boxes (stage markers) instead
      // .on('click', ...) - commented out

    // Add link labels with original URLs
    link.append('title').text((d: any) => {
      const sourceNode = optimizedNodes[d.source.index];
      const targetNode = optimizedNodes[d.target.index];
      return `${(sourceNode as any).originalName || sourceNode.name}\n↓\n${
        (targetNode as any).originalName || targetNode.name
      }\n\n${d.value.toLocaleString()} sessions`;
    });

    // Draw nodes with better visibility
    const node = g
      .append('g')
      .selectAll('rect')
      .data(sankeyNodes)
      .enter()
      .append('rect')
      .attr('x', (d: any) => d.x0)
      .attr('y', (d: any) => d.y0)
      .attr('height', (d: any) => Math.max(10, d.y1 - d.y0)) // Minimum height 10px
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('fill', (d: any) => nodeColorMap.get(d.name) || colorPalette[0]) // Use stable color!
      .attr('opacity', 0.9) // More opaque for better visibility
      .attr('class', 'sankey-node')
      .attr('rx', 4) // Rounded corners
      .attr('ry', 4)
      .style('cursor', 'pointer')
      .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))') // Shadow for depth
      .on('mouseover', function () {
        d3.select(this)
          .attr('opacity', 1)
          .style('filter', 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'); // Stronger shadow on hover
      })
      .on('mouseout', function () {
        d3.select(this)
          .attr('opacity', 0.9)
          .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))');
      })
      .on('click', (event, d: any) => {
        if (onNodeClickRef.current) {
          const originalNode = nodes[d.index];
          onNodeClickRef.current(originalNode);
        }
      });

    // Add node labels with better formatting and responsive font size
    const labelFontSize = Math.max(12, Math.min(16, chartWidth / 60)); // Responsive font size

    // Calculate unique users (sessions) per node
    const nodeSessionCounts = new Map<number, number>();
    sankeyLinks.forEach((link: any) => {
      // Count incoming sessions for each node
      const targetIndex = link.target.index;
      nodeSessionCounts.set(targetIndex, (nodeSessionCounts.get(targetIndex) || 0) + link.value);
    });
    // Also count source nodes
    sankeyLinks.forEach((link: any) => {
      const sourceIndex = link.source.index;
      if (!nodeSessionCounts.has(sourceIndex)) {
        nodeSessionCounts.set(sourceIndex, 0);
      }
      nodeSessionCounts.set(sourceIndex, nodeSessionCounts.get(sourceIndex)! + link.value);
    });

    // Create label groups for main label + subtitle
    const labelGroup = g
      .append('g')
      .selectAll('g')
      .data(sankeyNodes)
      .enter()
      .append('g')
      .attr('transform', (d: any) => {
        const x = d.x0 < chartWidth / 2 ? d.x1 + 10 : d.x0 - 10;
        const y = (d.y1 + d.y0) / 2;
        return `translate(${x}, ${y})`;
      })
      .style('cursor', 'pointer')
      .style('pointer-events', 'all')
      .on('click', (event, d: any) => {
        if (onNodeClickRef.current) {
          // **FIXED: Pass full node data including sessions array**
          const originalNode = nodes[d.index];
          onNodeClickRef.current(originalNode);
        }
      });

    // Main label (page name) - SMART: Show beginning (most important part)
    labelGroup
      .append('text')
      .attr('dy', '-0.2em') // Offset up to make room for subtitle
      .attr('text-anchor', (d: any) => (d.x0 < chartWidth / 2 ? 'start' : 'end'))
      .text((d: any) => {
        const name = d.name;

        // Calculate dynamic max length based on node count and screen width
        const maxLength = Math.max(
          20,
          Math.min(35, Math.floor(chartWidth / (sankeyNodes.length * 1.5)))
        );

        // **CRITICAL FIX: Truncate from END, show BEGINNING (most important)**
        if (name.length > maxLength) {
          // Keep the first part which is usually the most meaningful
          // For example: "Buy Mounjaro Pen for Weight Loss..." instead of "...Loss Online | Quick Meds"
          return name.substring(0, maxLength) + '…';
        }

        return name;
      })
      .attr('font-size', `${Math.max(11, Math.min(13, chartWidth / 70))}px`) // Moderate size (11-13px)
      .attr('font-weight', '600') // Semi-bold (not too heavy)
      .attr('fill', isDarkMode ? '#F3F4F6' : '#1F2937') // High contrast
      .style(
        'text-shadow',
        isDarkMode
          ? '0 1px 2px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.6)' // Moderate shadow for dark mode
          : '0 1px 2px rgba(255,255,255,0.95), 0 0 4px rgba(255,255,255,0.8)' // Moderate white glow for light mode
      )
      .each(function (d: any) {
        // Add title attribute for hover tooltip with FULL text
        d3.select(this).append('title').text(d.name);
      });

    // Subtitle (sessions, users, UTM, and campaign) - ENHANCED: Show website & campaign origin
    labelGroup
      .append('text')
      .attr('dy', '1em') // Offset down below main label
      .attr('text-anchor', (d: any) => (d.x0 < chartWidth / 2 ? 'start' : 'end'))
      .text((d: any) => {
        const originalNode = optimizedNodes[d.index];

        // **FIXED: Use journeyCount (stitched journeys) for display, not raw session count**
        // originalNode.journeyCount = number of unique stitched journeys (CORRECT for display)
        // originalNode.sessions = ALL session IDs including stitched (for drill-down)
        const uniqueJourneys = originalNode.journeyCount || originalNode.sessions?.length || 0;

        // Estimate unique users (rough: journeys / 1.5)
        const uniqueUsers = Math.floor(uniqueJourneys / 1.5);

        // Get top UTM source
        const topUtmSource =
          originalNode.utmSources && originalNode.utmSources.length > 0
            ? originalNode.utmSources.sort((a: any, b: any) => b.count - a.count)[0].source
            : null;

        // Get top UTM campaign
        const topCampaign =
          originalNode.utmCampaigns && originalNode.utmCampaigns.length > 0
            ? originalNode.utmCampaigns.sort((a: any, b: any) => b.count - a.count)[0].campaign
            : null;

        // **DEBUG: Log node data to see what we're getting**
        if (d.index === 0) {
          console.log('🔍 First node data:', {
            id: originalNode.id,
            name: originalNode.name,
            sessions: originalNode.sessions?.length,
            utmSources: originalNode.utmSources,
            utmCampaigns: originalNode.utmCampaigns,
          });
        }

        // Extract website from node ID (URL)
        let website = '';
        try {
          // Node ID is the full URL
          const nodeId = originalNode.id;

          // Check if it's a full URL with protocol
          if (nodeId.includes('://')) {
            // Handle malformed URLs where https:// might be embedded in query params
            // Extract the first valid URL part
            const urlMatch = nodeId.match(/^(https?:\/\/[^?]+)/);
            if (urlMatch) {
              const url = new URL(urlMatch[1]);
              website = url.hostname.replace('www.', '');
            }
          } else if (nodeId.startsWith('/')) {
            // Relative URL - try to extract domain from query params if present
            const domainMatch = nodeId.match(/quickmeds\.co\.uk|ulp\.quickmeds\.co\.uk/);
            if (domainMatch) {
              website = domainMatch[0];
            } else {
              website = '';
            }
          } else {
            // Try to extract domain
            const match = nodeId.match(/([a-z0-9-]+\.[a-z]{2,})/i);
            if (match) website = match[1].replace('www.', '');
          }
        } catch (e) {
          // Silently handle URL parsing errors - not critical for display
          website = '';
        }

        // Build compact subtitle with ALL available info
        // Note: uniqueUsers is just an estimate (journeys / 1.5), not actual stitched count
        const parts = [`${uniqueJourneys.toLocaleString()}s`, `~${uniqueUsers.toLocaleString()}u`];

        // ALWAYS show website if we have it and it's not the main one
        if (website && website !== 'quickmeds.co.uk' && website !== 'quickmeds') {
          parts.push(`🌐${website}`);
        }

        // ALWAYS show UTM source if we have it
        if (topUtmSource && topUtmSource !== 'direct') {
          parts.push(`📍${topUtmSource}`);
        }

        // ALWAYS show campaign if we have it
        if (topCampaign) {
          const shortCampaign =
            topCampaign.length > 15 ? topCampaign.substring(0, 15) + '…' : topCampaign;
          parts.push(`🎯${shortCampaign}`);
        }

        return parts.join(' · ');
      })
      .attr('font-size', `${Math.max(9, Math.min(11, labelFontSize * 0.7))}px`) // Smaller: 9-11px
      .attr('font-weight', '400') // Regular weight
      .attr('fill', isDarkMode ? '#9CA3AF' : '#6B7280') // Light grey
      .attr('opacity', 0.8);

    // Add node tooltips with COMPREHENSIVE stats (sessions, users, UTM breakdown)
    node.append('title').text((d: any) => {
      const nodeData = optimizedNodes[d.index];
      const originalName = (nodeData as any).originalName || nodeData.name;
      
      // **FIXED: Calculate incoming/outgoing based on unique sessions (link.value now counts unique sessions only)**
      const totalIn = sankeyLinks
        .filter((l: any) => l.target.index === d.index)
        .reduce((sum: number, l: any) => sum + l.value, 0);
      const totalOut = sankeyLinks
        .filter((l: any) => l.source.index === d.index)
        .reduce((sum: number, l: any) => sum + l.value, 0);
      
      // Total unique stitched journeys at this node
      const totalJourneys = nodeData.journeyCount || nodeData.sessions?.length || 0;
      const totalSessionIds = nodeData.sessions?.length || 0;

      // Build detailed tooltip with UTM breakdown and website info
      let tooltip = `${originalName}\n`;

      // Add website/URL info
      tooltip += `🌐 URL: ${nodeData.id}\n\n`;

      tooltip += `📊 Unique Journeys: ${totalJourneys.toLocaleString()}\n`;
      if (totalSessionIds > totalJourneys) {
        tooltip += `   (from ${totalSessionIds} raw sessions - stitched!)\n`;
      }
      tooltip += `👥 Est. Users: ${Math.floor(totalJourneys / 1.5)}\n\n`;
      
      // Show incoming/outgoing flow
      if (totalIn > 0) {
        tooltip += `📥 Incoming: ${totalIn.toLocaleString()} sessions\n`;
      }
      if (totalOut > 0) {
        tooltip += `📤 Outgoing: ${totalOut.toLocaleString()} sessions\n`;
      }
      
      // Calculate drop-off if there are both incoming and outgoing
      if (totalIn > 0 && totalOut > 0) {
        const dropOff = totalIn - totalOut;
        const dropOffPercent = ((dropOff / totalIn) * 100).toFixed(1);
        if (dropOff > 0) {
          tooltip += `⚠️ Drop-off: ${dropOff.toLocaleString()} sessions (${dropOffPercent}%)\n`;
        }
      }

      // Add UTM sources breakdown
      if (nodeData.utmSources && nodeData.utmSources.length > 0) {
        tooltip += `\n📍 Traffic Sources:\n`;
        nodeData.utmSources
          .sort((a: any, b: any) => b.count - a.count)
          .forEach((s: any) => {
            const percentage = ((s.count / (totalJourneys || 1)) * 100).toFixed(1);
            tooltip += `   • ${s.source}: ${s.count} journeys (${percentage}%)\n`;
          });
      } else {
        tooltip += `\n📍 Traffic Sources: Direct/Unknown\n`;
      }

      // Add UTM campaigns breakdown
      if (nodeData.utmCampaigns && nodeData.utmCampaigns.length > 0) {
        tooltip += `\n🎯 Campaigns:\n`;
        nodeData.utmCampaigns
          .sort((a: any, b: any) => b.count - a.count)
          .forEach((c: any) => {
            const percentage = ((c.count / (totalJourneys || 1)) * 100).toFixed(1);
            tooltip += `   • ${c.campaign}: ${c.count} journeys (${percentage}%)\n`;
          });
      } else {
        tooltip += `\n🎯 Campaigns: None tracked\n`;
      }

      return tooltip;
    });
  }, [optimizedData, dimensions]);

  // Debug dimensions
  console.log(
    `🖼️ SankeyDiagram render: dimensions.width = ${dimensions.width}px, height = ${height}px`
  );

  return (
    <div className="w-full">
      {/* Horizontally scrollable container for wide diagrams */}
      <div
        ref={containerRef}
        className="overflow-x-auto overflow-y-hidden bg-white dark:bg-secondary-900 rounded-lg shadow-inner scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-200 dark:scrollbar-track-gray-800"
        style={{
          minHeight: `${height}px`,
          maxHeight: `${height}px`,
          width: '100%',
        }}
      >
        {/* Inner wrapper that expands to SVG width - CRITICAL for horizontal scroll */}
        <div
          style={{
            minWidth: `${dimensions.width}px`, // Match SVG width
            width: `${dimensions.width}px`, // Force container to SVG width
            height: `${height}px`,
          }}
        >
          <svg
            ref={svgRef}
            className="sankey-svg"
            style={{
              width: `${dimensions.width}px`, // Explicit SVG width
              height: `${height}px`,
              display: 'block',
            }}
          ></svg>
        </div>
      </div>
      {hoveredLink && (
        <div className="mt-3 text-sm text-secondary-600 dark:text-secondary-400 text-center">
          💡 Click on flows to see session details • Scroll horizontally to see all stages
        </div>
      )}
      <style>{`
        .sankey-link {
          transition: all 0.2s ease;
        }
        .sankey-link:hover {
          stroke-opacity: 0.8 !important;
        }
        .sankey-node {
          transition: all 0.2s ease;
        }
        .sankey-node:hover {
          opacity: 1 !important;
          filter: brightness(1.1);
        }
        .sankey-svg {
          display: block;
        }
        
        /* Custom scrollbar styling */
        .scrollbar-thin::-webkit-scrollbar {
          height: 12px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: var(--scrollbar-track, #e5e7eb);
          border-radius: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: var(--scrollbar-thumb, #9ca3af);
          border-radius: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: var(--scrollbar-thumb-hover, #6b7280);
        }
      `}</style>
    </div>
  );
};

export default SankeyDiagram;
